import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  X, Search, User, Phone, Star, Check, UserX, Loader2,
  UserPlus, Calendar, Gift, ArrowLeft, Sparkles
} from 'lucide-react';
import { useCustomer } from '../../../hooks/useCustomer';
import { toast } from 'react-hot-toast';

export default function CustomerSearchModal({
  isOpen,
  onClose,
  onSelectCustomer,
  linkedCustomer,
  onUnlinkCustomer
}) {
  const { customers, loading: loadingCustomers, fetchCustomers, createCustomer } = useCustomer();

  const [searchQuery, setSearchQuery] = useState('');
  const [filteredCustomers, setFilteredCustomers] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [isSearching, setIsSearching] = useState(false);

  // New customer form state
  const [showNewCustomerForm, setShowNewCustomerForm] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [newCustomer, setNewCustomer] = useState({
    name: '',
    phone: '',
    dateOfBirth: '',
    anniversaryDate: ''
  });
  const [formErrors, setFormErrors] = useState({});

  const searchInputRef = useRef(null);
  const phoneInputRef = useRef(null);

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      fetchCustomers();
      setSearchQuery('');
      setFilteredCustomers([]);
      setSelectedCustomer(null);
      setShowNewCustomerForm(false);
      setNewCustomer({ name: '', phone: '', dateOfBirth: '', anniversaryDate: '' });
      setFormErrors({});
      // Focus search input after a short delay
      setTimeout(() => searchInputRef.current?.focus(), 100);
    }
  }, [isOpen]);

  // Filter customers based on search query (local filtering)
  useEffect(() => {
    if (!searchQuery.trim()) {
      setFilteredCustomers(customers.slice(0, 20));
      return;
    }

    setIsSearching(true);
    const query = searchQuery.toLowerCase().trim();

    // Filter from loaded customers
    const filtered = customers.filter(customer => {
      const nameMatch = customer.name?.toLowerCase().includes(query);
      const phoneMatch = customer.phone?.includes(query);
      return nameMatch || phoneMatch;
    });

    // Sort: exact phone matches first, then name matches
    filtered.sort((a, b) => {
      const aPhoneExact = a.phone === query;
      const bPhoneExact = b.phone === query;
      if (aPhoneExact && !bPhoneExact) return -1;
      if (!aPhoneExact && bPhoneExact) return 1;

      const aPhoneStart = a.phone?.startsWith(query);
      const bPhoneStart = b.phone?.startsWith(query);
      if (aPhoneStart && !bPhoneStart) return -1;
      if (!aPhoneStart && bPhoneStart) return 1;

      return 0;
    });

    setFilteredCustomers(filtered.slice(0, 10)); // Limit to 10 results
    setIsSearching(false);
  }, [searchQuery, customers]);

  // Handle search input change
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchQuery(value);
    setSelectedCustomer(null);
  };

  // Handle select customer
  const handleSelect = () => {
    if (selectedCustomer) {
      onSelectCustomer(selectedCustomer);
    }
  };

  // Validate new customer form
  const validateForm = () => {
    const errors = {};

    if (!newCustomer.name.trim()) {
      errors.name = 'Name is required';
    }

    if (!newCustomer.phone.trim()) {
      errors.phone = 'Phone number is required';
    } else if (!/^\d{10}$/.test(newCustomer.phone.replace(/\D/g, ''))) {
      errors.phone = 'Enter valid 10-digit phone';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Handle create new customer
  const handleCreateCustomer = async () => {
    if (!validateForm()) return;

    setIsCreating(true);
    try {
      const customerData = {
        name: newCustomer.name.trim(),
        phone: newCustomer.phone.replace(/\D/g, ''),
        dateOfBirth: newCustomer.dateOfBirth || null,
        anniversaryDate: newCustomer.anniversaryDate || null,
        customerType: 'regular',
        loyaltyPoints: 0,
        isActive: true
      };

      const created = await createCustomer(customerData);

      // Select the newly created customer (toast already shown by useCustomer hook)
      onSelectCustomer(created);

    } catch (error) {
      console.error('Failed to create customer:', error);
      // Toast already shown by useCustomer hook
    } finally {
      setIsCreating(false);
    }
  };

  // Switch to new customer form with phone pre-filled
  const handleAddNewCustomer = () => {
    setShowNewCustomerForm(true);
    setNewCustomer(prev => ({
      ...prev,
      phone: searchQuery.replace(/\D/g, '') // Pre-fill phone if user searched by number
    }));
    setTimeout(() => {
      if (searchQuery && /^\d+$/.test(searchQuery)) {
        // If search was a phone number, focus on name
        document.getElementById('customer-name-input')?.focus();
      } else {
        phoneInputRef.current?.focus();
      }
    }, 100);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md max-h-[85vh] flex flex-col overflow-hidden">

        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200 bg-gradient-to-r from-teal-50 to-cyan-50">
          <div className="flex items-center gap-3">
            {showNewCustomerForm && (
              <button
                onClick={() => setShowNewCustomerForm(false)}
                className="p-1.5 hover:bg-white rounded-lg transition-all"
              >
                <ArrowLeft size={18} className="text-slate-600" />
              </button>
            )}
            <div className="w-10 h-10 bg-teal-500 rounded-xl flex items-center justify-center shadow-sm">
              {showNewCustomerForm ? (
                <UserPlus size={20} className="text-white" />
              ) : (
                <User size={20} className="text-white" />
              )}
            </div>
            <div>
              <h2 className="text-lg font-bold text-slate-800">
                {showNewCustomerForm ? 'New Customer' : 'Link Customer'}
              </h2>
              <p className="text-xs text-slate-500">
                {showNewCustomerForm ? 'Enter customer details' : 'Search by phone or name'}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-white rounded-xl transition-all"
          >
            <X size={20} className="text-slate-500" />
          </button>
        </div>

        {/* Content */}
        {!showNewCustomerForm ? (
          <>
            {/* Currently Linked Customer */}
            {linkedCustomer && (
              <div className="mx-4 mt-4 p-3 bg-teal-50 border border-teal-200 rounded-xl">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 bg-teal-500 rounded-full flex items-center justify-center">
                      <User size={16} className="text-white" />
                    </div>
                    <div>
                      <p className="font-bold text-slate-800">{linkedCustomer.name}</p>
                      <div className="flex items-center gap-2 text-xs text-slate-500">
                        <span>{linkedCustomer.phone}</span>
                        {linkedCustomer.loyaltyPoints > 0 && (
                          <span className="flex items-center gap-0.5 text-amber-600 font-bold">
                            <Star size={10} fill="currentColor" />
                            {linkedCustomer.loyaltyPoints} pts
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={() => {
                      onUnlinkCustomer();
                      toast.success('Customer unlinked');
                    }}
                    className="px-3 py-1.5 bg-red-100 hover:bg-red-200 text-red-600 text-xs font-semibold rounded-lg transition-all flex items-center gap-1"
                  >
                    <UserX size={12} />
                    Unlink
                  </button>
                </div>
              </div>
            )}

            {/* Search Input */}
            <div className="p-4">
              <div className="relative">
                <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  ref={searchInputRef}
                  type="text"
                  placeholder="Enter phone number or name..."
                  value={searchQuery}
                  onChange={handleSearchChange}
                  className="w-full pl-11 pr-4 py-3 border-2 border-slate-200 rounded-xl focus:border-teal-500 focus:ring-2 focus:ring-teal-100 outline-none transition-all text-sm font-medium"
                  autoFocus
                />
                {isSearching && (
                  <Loader2 size={18} className="absolute right-4 top-1/2 -translate-y-1/2 text-teal-600 animate-spin" />
                )}
              </div>
            </div>

            {/* Customer List */}
            <div className="flex-1 overflow-y-auto px-4 pb-4">
              {loadingCustomers ? (
                <div className="flex flex-col items-center justify-center py-12">
                  <Loader2 size={32} className="text-teal-500 animate-spin mb-3" />
                  <p className="text-sm text-slate-500">Loading customers...</p>
                </div>
) : filteredCustomers.length > 0 ? (
  <div className="space-y-2">
    {filteredCustomers.map((customer) => {
      const isSelected = selectedCustomer?.id === customer.id;
      const isLinked = linkedCustomer?.id === customer.id;

      return (
        <button
          key={customer.id}
          onClick={() => !isLinked && setSelectedCustomer(customer)}
          disabled={isLinked}
          className={`w-full p-3 rounded-xl border-2 transition-all text-left ${
            isLinked
              ? 'border-teal-300 bg-teal-50 cursor-default'
              : isSelected
              ? 'border-teal-500 bg-teal-50 shadow-md'
              : 'border-slate-200 hover:border-teal-300 bg-white hover:bg-slate-50'
          }`}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-bold ${
                isLinked ? 'bg-teal-400' : isSelected ? 'bg-teal-500' : 'bg-slate-400'
              }`}>
                {customer.name?.charAt(0)?.toUpperCase() || 'C'}
              </div>
              <div>
                <p className="font-bold text-slate-800">{customer.name}</p>
                <div className="flex items-center gap-3 mt-0.5">
                  <span className="flex items-center gap-1 text-xs text-slate-500">
                    <Phone size={10} />
                    {customer.phone}
                  </span>
                  {customer.loyaltyPoints > 0 && (
                    <span className="flex items-center gap-0.5 text-xs text-amber-600 font-bold">
                      <Star size={10} fill="currentColor" />
                      {customer.loyaltyPoints} pts
                    </span>
                  )}
                </div>
              </div>
            </div>
            {isSelected && !isLinked && (
              <div className="w-7 h-7 bg-teal-500 rounded-full flex items-center justify-center">
                <Check size={16} className="text-white" />
              </div>
            )}
            {isLinked && (
              <span className="text-xs bg-teal-200 text-teal-700 px-2 py-1 rounded-full font-semibold">
                Linked
              </span>
            )}
          </div>
        </button>
      );
    })}

    {/* Add New Customer Option */}
    <button
      onClick={handleAddNewCustomer}
      className="w-full p-3 rounded-xl border-2 border-dashed border-teal-300 bg-teal-50/50 hover:bg-teal-50 hover:border-teal-400 transition-all text-left"
    >
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-full bg-teal-100 flex items-center justify-center">
          <UserPlus size={18} className="text-teal-600" />
        </div>
        <div>
          <p className="font-bold text-teal-700">Create New Customer</p>
          <p className="text-xs text-teal-600">
            {searchQuery ? `Add "${searchQuery}" as new customer` : 'Add a new customer'}
          </p>
        </div>
      </div>
    </button>
  </div>
) : (
  <div className="text-center py-8">
    <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
      <Search size={28} className="text-slate-400" />
    </div>
    <p className="text-slate-600 font-semibold mb-1">No customers found</p>
    <p className="text-sm text-slate-500 mb-4">
      No match for "{searchQuery}"
    </p>
    <button
      onClick={handleAddNewCustomer}
      className="inline-flex items-center gap-2 px-4 py-2.5 bg-teal-500 hover:bg-teal-600 text-white rounded-xl font-semibold text-sm transition-all"
    >
      <UserPlus size={16} />
      Create New Customer
    </button>
  </div>
)}
            </div>

            {/* Footer */}
            <div className="border-t-2 border-slate-100 p-4 bg-slate-50">
              <div className="flex gap-3">
                <button
                  onClick={onClose}
                  className="flex-1 px-4 py-2.5 bg-white text-slate-600 rounded-xl font-semibold hover:bg-slate-100 transition-all border-2 border-slate-200"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSelect}
                  disabled={!selectedCustomer || linkedCustomer?.id === selectedCustomer?.id}
                  className="flex-1 px-4 py-2.5 bg-teal-500 hover:bg-teal-600 text-white rounded-xl font-bold transition-all disabled:bg-slate-300 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  <Check size={18} />
                  Link Customer
                </button>
              </div>
            </div>
          </>
        ) : (
          /* New Customer Form */
          <>
            <div className="flex-1 overflow-y-auto p-4">
              <div className="space-y-4">
                {/* Name Field */}
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-1.5">
                    Customer Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    id="customer-name-input"
                    type="text"
                    placeholder="Enter full name"
                    value={newCustomer.name}
                    onChange={(e) => {
                      setNewCustomer(prev => ({ ...prev, name: e.target.value }));
                      setFormErrors(prev => ({ ...prev, name: null }));
                    }}
                    className={`w-full px-4 py-3 border-2 rounded-xl focus:ring-2 focus:ring-teal-100 outline-none transition-all text-sm ${
                      formErrors.name ? 'border-red-300 focus:border-red-500' : 'border-slate-200 focus:border-teal-500'
                    }`}
                  />
                  {formErrors.name && (
                    <p className="text-xs text-red-500 mt-1">{formErrors.name}</p>
                  )}
                </div>

                {/* Phone Field */}
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-1.5">
                    Phone Number <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <Phone size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
                    <input
                      ref={phoneInputRef}
                      type="tel"
                      placeholder="10-digit mobile number"
                      value={newCustomer.phone}
                      onChange={(e) => {
                        const value = e.target.value.replace(/\D/g, '').slice(0, 10);
                        setNewCustomer(prev => ({ ...prev, phone: value }));
                        setFormErrors(prev => ({ ...prev, phone: null }));
                      }}
                      className={`w-full pl-11 pr-4 py-3 border-2 rounded-xl focus:ring-2 focus:ring-teal-100 outline-none transition-all text-sm ${
                        formErrors.phone ? 'border-red-300 focus:border-red-500' : 'border-slate-200 focus:border-teal-500'
                      }`}
                    />
                  </div>
                  {formErrors.phone && (
                    <p className="text-xs text-red-500 mt-1">{formErrors.phone}</p>
                  )}
                </div>

                {/* Optional Fields Section */}
                <div className="pt-2">
                  <p className="text-xs font-medium text-slate-400 uppercase tracking-wide mb-3">
                    Optional Details
                  </p>

                  <div className="grid grid-cols-2 gap-3">
                    {/* Date of Birth */}
                    <div>
                      <label className="block text-sm font-medium text-slate-600 mb-1.5">
                        <Calendar size={12} className="inline mr-1" />
                        Date of Birth
                      </label>
                      <input
                        type="date"
                        value={newCustomer.dateOfBirth}
                        onChange={(e) => setNewCustomer(prev => ({ ...prev, dateOfBirth: e.target.value }))}
                        className="w-full px-3 py-2.5 border-2 border-slate-200 rounded-xl focus:border-teal-500 focus:ring-2 focus:ring-teal-100 outline-none transition-all text-sm"
                      />
                    </div>

                    {/* Anniversary */}
                    <div>
                      <label className="block text-sm font-medium text-slate-600 mb-1.5">
                        <Gift size={12} className="inline mr-1" />
                        Anniversary
                      </label>
                      <input
                        type="date"
                        value={newCustomer.anniversaryDate}
                        onChange={(e) => setNewCustomer(prev => ({ ...prev, anniversaryDate: e.target.value }))}
                        className="w-full px-3 py-2.5 border-2 border-slate-200 rounded-xl focus:border-teal-500 focus:ring-2 focus:ring-teal-100 outline-none transition-all text-sm"
                      />
                    </div>
                  </div>
                </div>

                {/* Info Box */}
                <div className="bg-slate-50 rounded-xl p-3 border border-slate-200">
                  <p className="text-xs text-slate-500">
                    <span className="font-semibold text-slate-600">Tip:</span> Customer will start with 0 loyalty points.
                    Points can be earned on future orders.
                  </p>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="border-t-2 border-slate-100 p-4 bg-slate-50">
              <div className="flex gap-3">
                <button
                  onClick={() => setShowNewCustomerForm(false)}
                  className="flex-1 px-4 py-2.5 bg-white text-slate-600 rounded-xl font-semibold hover:bg-slate-100 transition-all border-2 border-slate-200"
                >
                  Back
                </button>
                <button
                  onClick={handleCreateCustomer}
                  disabled={isCreating}
                  className="flex-1 px-4 py-2.5 bg-teal-500 hover:bg-teal-600 text-white rounded-xl font-bold transition-all disabled:bg-teal-300 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {isCreating ? (
                    <>
                      <Loader2 size={18} className="animate-spin" />
                      Creating...
                    </>
                  ) : (
                    <>
                      <UserPlus size={18} />
                      Create & Link
                    </>
                  )}
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
